import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { trainingAPI } from "@api/TrainingAPI";

export const UDF_SET_FLAGS = "UDF_SET_FLAGS";
export const UDF_SET_LOADING = "UDF_SET_LOADING";

export const setUserDefinedFlags = (data: any[]) => ({
    type: UDF_SET_FLAGS,
    payload: data,
});

export const getUserDefinedFlags = () => async (dispatch: any) => {
    dispatch({ type: UDF_SET_LOADING, payload: true });
    try {
        const data = await trainingAPI.getUserDefinedFlags();
        dispatch(setUserDefinedFlags(data));
    } catch {
        message.error("Failed to load user defined flags");
    } finally {
        dispatch({ type: UDF_SET_LOADING, payload: false });
    }
};

const nullIfBlank = (s: string | undefined | null) => (s?.trim() === "" ? null : s);

const sanitizeFlagData = (flagData: any) => ({
    ...flagData,
    name: nullIfBlank(flagData.name),
    abbreviation: nullIfBlank(flagData.abbreviation),
    description: nullIfBlank(flagData.description),
});

const isUnauthorized = (e: any) => e?.response?.status === 403 || e?.response?.status === 401;

const getFormattedErrorMessage = (e: any): string => {
    if (isUnauthorized(e)) return "Unauthorized";

    if (e?.response?.status === 400) {
        const errorData = e?.response?.data;
        let errorMsg = null;

        if (typeof errorData === "string") {
            errorMsg = errorData;
        } else if (typeof errorData === "object") {
            errorMsg = errorData?.message || errorData?.error || errorData?.detail;
        }

        if (typeof errorMsg === "string" && errorMsg.trim()) {
            let formatted = errorMsg.trim();
            formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
            formatted = formatted.replace(/\bcustom\b/gi, "user defined");
            return formatted;
        }
    }

    return "Failed to create flag";
};

export const createUserDefinedFlag = (flagData: any) => async (dispatch: any): Promise<boolean> => {
    try {
        await trainingAPI.createUserDefinedFlag(sanitizeFlagData(flagData));
        message.success("Flag created successfully");
        dispatch(getUserDefinedFlags());
        return true;
    } catch (e) {
        message.error(getFormattedErrorMessage(e));
        return false;
    }
};

export const updateUserDefinedFlag = (id: number, flagData: any) => async (dispatch: any): Promise<boolean> => {
    try {
        await trainingAPI.updateUserDefinedFlag(id, sanitizeFlagData(flagData));
        message.success("Flag updated successfully");
        dispatch(getUserDefinedFlags());
        return true;
    } catch (e: any) {
        if (isUnauthorized(e)) {
            message.error("Unauthorized");
        } else if (e?.response?.status === 400) {
            const errorData = e?.response?.data;
            let errorMsg = null;
            if (typeof errorData === "string") {
                errorMsg = errorData;
            } else if (typeof errorData === "object") {
                errorMsg = errorData?.message || errorData?.error || errorData?.detail;
            }
            if (typeof errorMsg === "string" && errorMsg.trim()) {
                let formatted = errorMsg.trim();
                formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
                formatted = formatted.replace(/\bcustom\b/gi, "user defined");
                message.error(formatted);
            } else {
                message.error("Failed to update flag");
            }
        } else {
            message.error("Failed to update flag");
        }
        return false;
    }
};

export const deleteUserDefinedFlag = (id: number) => async (dispatch: any) => {
    try {
        await trainingAPI.deleteUserDefinedFlag(id);
        message.success("Flag deleted successfully");
        dispatch(getUserDefinedFlags());
    } catch (e: any) {
        if (isUnauthorized(e)) {
            message.error("Unauthorized");
        } else if (e?.response?.status === 400) {
            const errorData = e?.response?.data;
            let errorMsg = null;
            if (typeof errorData === "string") {
                errorMsg = errorData;
            } else if (typeof errorData === "object") {
                errorMsg = errorData?.message || errorData?.error || errorData?.detail;
            }
            if (typeof errorMsg === "string" && errorMsg.trim()) {
                let formatted = errorMsg.trim();
                formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
                formatted = formatted.replace(/\bcustom\b/gi, "user defined");
                message.error(formatted);
            } else {
                message.error("Failed to delete flag");
            }
        } else {
            message.error("Failed to delete flag");
        }
    }
};
